/**
 * Centralized API configuration
 * Uses environment variable VITE_API_URL or falls back to relative path for production
 */

// Get API URL from environment or use relative path (works with nginx proxy)
export const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== "undefined" ? `${window.location.origin}/api` : "/api");

// Helper to build API endpoints
export const apiUrl = (path: string): string => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${cleanPath}`;
};

// Specific API endpoints
export const API_ENDPOINTS = {
  // Schema endpoints
  schemaReferences: () => apiUrl("/schema/references"),
  schemaBusinessActions: (module?: string) =>
    module
      ? apiUrl(`/schema/business-actions/${module}`)
      : apiUrl("/schema/business-actions"),
  schemaRefreshBusinessActions: () =>
    apiUrl("/schema/refresh-business-actions"),

  // Module endpoints
  moduleConfig: (module: string) => apiUrl(`/modules/${module}/config`),

  // Direct query endpoints
  directQuery: () => apiUrl("/direct/query"),
  directUnsetRelation: () => apiUrl("/direct/unset-relation"),

  // Business actions
  businessActionsExecute: () => apiUrl("/business-actions/execute"),

  // Tools
  toolsCall: () => apiUrl("/tools/call"),

  // Schema refresh
  schemaRefreshReferences: (entity?: string) =>
    entity
      ? apiUrl(`/schema/references/refresh?entity=${entity}`)
      : apiUrl("/schema/references/refresh"),
} as const;

/**
 * Refresh reference cache for a specific entity.
 * Call this after creating new records in main entities.
 */
export const refreshReferences = async (entity?: string): Promise<void> => {
  try {
    await fetch(API_ENDPOINTS.schemaRefreshReferences(entity), {
      method: "POST",
    });
  } catch (e) {
    console.warn("Failed to refresh references:", e);
  }
};

/**
 * Map of coreBOS module names to reference entity names.
 * Only these entities have cached references that need refreshing.
 */
export const REFERENCE_ENTITIES: Record<string, string> = {
  Accounts: "Accounts",
  Contacts: "Contacts",
  Project: "Project",
  Potentials: "Potentials",
  Users: "Users",
  HelpDesk: "HelpDesk",
  cbOffers: "cbOffers",
  cbSOMaster: "cbSOMaster",
  InvoiceMaster: "InvoiceMaster",
};
